import { useMemo } from 'react';
import { useMatches } from 'react-router-dom';

import { useAppSelector } from '@/store/hooks';
import { getUser } from '@/store/slices/user/selectors';
import { EPermissions } from '@/types/permissions.types';
import { EDIT_PROJECT_ROUTE_IDS } from './edit-project-route-ids';

type ReadOnlyRouteAccess = {
  /** Routes the role is allowed to open. Everything else sends them back to the overview. */
  navigable: string[];
  /** Routes the role is still allowed to act on. Everything else renders behind a read-only shield. */
  interactive: string[];
};

const READ_ONLY_ROUTE_ACCESS: Partial<Record<EPermissions, ReadOnlyRouteAccess>> = {
  [EPermissions.Analytics]: {
    navigable: [EDIT_PROJECT_ROUTE_IDS.OVERVIEW],
    interactive: [],
  },
  [EPermissions.PasscodeManager]: {
    navigable: [EDIT_PROJECT_ROUTE_IDS.OVERVIEW, EDIT_PROJECT_ROUTE_IDS.AUDIENCES],
    interactive: [EDIT_PROJECT_ROUTE_IDS.AUDIENCES],
  },
};

export const READ_ONLY_PROJECT_ROLES = Object.keys(READ_ONLY_ROUTE_ACCESS) as EPermissions[];

export type ReadOnlyProjectAccess = {
  isReadOnly: boolean;
  canNavigateTo: (routeId?: string) => boolean;
  canInteractWith: (routeId?: string) => boolean;
};

const FULL_ACCESS: ReadOnlyProjectAccess = {
  isReadOnly: false,
  canNavigateTo: () => true,
  canInteractWith: () => true,
};

/* Any non-read-only role keeps full access, so every role held on the channel has to be a read-only one. */
const getReadOnlyProjectAccess = (channelRoles: string[]): ReadOnlyProjectAccess => {
  const isReadOnly =
    channelRoles.length > 0 && channelRoles.every((role) => READ_ONLY_PROJECT_ROLES.includes(role as EPermissions));

  if (!isReadOnly) {
    return FULL_ACCESS;
  }

  const navigableRouteIds = new Set<string>();
  const interactiveRouteIds = new Set<string>();

  for (const role of channelRoles) {
    const access = READ_ONLY_ROUTE_ACCESS[role as EPermissions];
    access?.navigable.forEach((routeId) => navigableRouteIds.add(routeId));
    access?.interactive.forEach((routeId) => interactiveRouteIds.add(routeId));
  }

  return {
    isReadOnly: true,
    canNavigateTo: (routeId?: string) => !!routeId && navigableRouteIds.has(routeId),
    canInteractWith: (routeId?: string) => !!routeId && interactiveRouteIds.has(routeId),
  };
};

export default function useReadOnlyProjectAccess(): ReadOnlyProjectAccess {
  const user = useAppSelector(getUser);

  return useMemo(() => getReadOnlyProjectAccess(user?.channels?.[user?.active_channel] ?? []), [user]);
}

export type ReadOnlyRouteState = ReadOnlyProjectAccess & {
  /** The current route is off limits and the user should be sent back to the overview. */
  isRouteBlocked: boolean;
  /** The current route can be viewed but not acted on. */
  isRouteReadOnly: boolean;
};

export function useReadOnlyRouteState(): ReadOnlyRouteState {
  const access = useReadOnlyProjectAccess();
  const matches = useMatches();

  /* Full access answers true for every route id, so these both fall out as false for everyone else. */
  return useMemo(
    () => ({
      ...access,
      isRouteBlocked: !matches.some((match) => access.canNavigateTo(match.id)),
      isRouteReadOnly: !matches.some((match) => access.canInteractWith(match.id)),
    }),
    [access, matches]
  );
}
